import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from 'react-toastify';
import {  createTitulo, updateTitulo, fetchTitulos } from '../actions/titulosActions';
import { fetchUniversidades } from '../actions/universidadActions';
import { fetchGrados } from '../actions/gradoActions';
import { Modal, Button, Form, Image } from 'react-bootstrap';
import { BsCamera, BsEye, BsEyeSlash } from 'react-icons/bs';
import { useInputFile } from "../hooks/useInputImage";
import { getUploadUrl } from '../util/utilFunctions';

const TitulosForm = ({ showFormModal, closeFormModal, titulo, socio }) => {
  const dispatch = useDispatch();
  const universidades = useSelector(state => state.universidad.universidades);
  const { error } = useSelector((state) => state.titulo); // Ajusta según tu estado global
  const grados = useSelector(state => state.grado.grados);

  const [tituloData, setTituloData] = useState({
    nombre: titulo.nombre || '',
    descrip: titulo.descrip || '',
    grado: titulo.grado || '',
    univor: titulo.univor || '',
    fechor: titulo.fechor ? new Date(titulo.fechor) : null,
    univrev: titulo.univrev || '',
    fechrev: titulo.fechrev ? new Date(titulo.fechrev) : null,
    fechapos: titulo.fechapos ? new Date(titulo.fechapos) : null,
    documpdf: titulo.documpdf || null,
    idcodreg: titulo.idcodreg || null
  });

  const [showCameraModal, setShowCameraModal] = useState(false);
  const [imageName, setImageName] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [imageVisible, setImageVisible] = useState(false);
  const videoRef = useRef(null);

  const {
    file: imagenFile,
    handleFileChange: handleImagenChange,
    resetInputFile: resetImagen,
    isValid: isImagenValid
  } = useInputFile();

  useEffect(() => {
    dispatch(fetchUniversidades());
    dispatch(fetchGrados());
  }, [dispatch]);

  useEffect(() => {
    setTituloData({
      nombre: titulo.nombre || '',
      descrip: titulo.descrip || '',
      grado: titulo.grado || '',
      univor: titulo.univor || '',
      fechor: titulo.fechor ? new Date(titulo.fechor) : null,
      univrev: titulo.univrev || '',
      fechrev: titulo.fechrev ? new Date(titulo.fechrev) : null,
      fechapos: titulo.fechapos ? new Date(titulo.fechapos) : null,
      documpdf: titulo.documpdf || null,
      idcodreg: titulo.idcodreg || null
    });

    if (titulo.documpdf && !(titulo.documpdf instanceof File)) {
      setImagePreview(getUploadUrl(titulo.documpdf));
    } else {
      setImagePreview(null);
    }
  }, [titulo]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTituloData(prev => ({ ...prev, [name]: value.toUpperCase() }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    handleImagenChange(file);

    if (file) {
      setImageName(file.name);
      setTituloData(prev => ({ ...prev, documpdf: file }));

      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const startCamera = () => {
    setShowCameraModal(true);
    navigator.mediaDevices.getUserMedia({ video: true })
      .then(stream => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      })
      .catch(err => {
        toast.error('Error al acceder a la cámara: ' + err.message);
      });
  };

  const captureImage = () => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
    const imageData = canvas.toDataURL('image/jpeg');
    const blob = dataURLtoBlob(imageData);

    const randomNum = Math.floor(Math.random() * 10000);
    const fileName = `${socio.apPat}_${randomNum}.jpg`;

    const file = new File([blob], fileName, { type: 'image/jpeg' });

    setTituloData(prev => ({ ...prev, documpdf: file }));
    setImagePreview(imageData);
    setImageName(fileName);
    setShowCameraModal(false);
    videoRef.current.srcObject.getTracks().forEach(track => track.stop());
  };

  const dataURLtoBlob = (dataURL) => {
    const byteString = atob(dataURL.split(',')[1]);
    const mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  };

  const handleDateChange = (name, date) => {
    setTituloData(prev => ({ ...prev, [name]: date }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tituloData.nombre || !tituloData.grado || !tituloData.univor || !tituloData.fechor) {
      toast.error("Por favor complete los campos requeridos: Título, grado, universidad de origen, fecha de expedición del título");
      return;
    }

    const formData = new FormData();
    Object.keys(tituloData).forEach(key => {
      if (key === 'idcodreg') {
        formData.append(key, socio.id);
      } else {
        if (tituloData[key] !== undefined && tituloData[key] !== null && tituloData[key] !== '') {
          if (key === 'fechor' || key === 'fechrev' || key === 'fechapos') {
            if (tituloData[key] !== null) {
              const newDate = new Date(tituloData[key]);
              formData.append(key, newDate.toISOString());
            }
          } else {
            formData.append(key, tituloData[key]);
          }
        } else {
          formData.append(key, null);
        }
      }
    });

    if (imagenFile) {
      formData.append('documpdf', imagenFile);
    }

    try {
      let result;
      if (titulo.id) {
        result = await dispatch(updateTitulo(titulo.id, formData));
      } else {
        result = await dispatch(createTitulo(formData));
      }

      // Verificar si hay un error en el estado
      if (!error) {
        toast.success(titulo.id ? 'Título actualizado exitosamente' : 'Título creado exitosamente');
        dispatch(fetchTitulos()); // Actualizar la lista de títulos
        closeFormModal(true); // Cerrar el modal
      } else {
        // Si hay un error, mostrar el mensaje de error
        toast.error(error || 'Error al procesar la solicitud');
      }
    } catch (error) {
      // Capturar errores inesperados
      toast.error('Error al procesar la solicitud');
    }
  };

  const toggleImageVisibility = () => {
    setImageVisible(!imageVisible);
  };

  return (
    <Modal
      show={showFormModal}
      onHide={() => closeFormModal(false)}
      backdrop="static"
      keyboard={false}
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>Formulario de Títulos</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {socio && (
          <div className="socio-info">
            <p><strong>Código de Registro:</strong> {socio.codreg}</p>
            <p><strong>Socio:</strong> {socio.apPat} {socio.apMat} {socio.nombres}</p>
          </div>
        )}
        <form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Título:</label>
            <input type="text" name="nombre" value={tituloData.nombre} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label>Descripción:</label>
            <input type="text" name="descrip" value={tituloData.descrip} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Grado:</label>
            <select name="grado" value={tituloData.grado} onChange={handleChange} required>
              <option value="">Seleccione un grado</option>
              {grados.map(grado => (
                <option key={grado.id} value={grado.id}>{grado.nombre}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Universidad Origen:</label>
            <select name="univor" value={tituloData.univor} onChange={handleChange}>
              <option value="">Seleccione una universidad</option>
              {universidades.map(universidad => (
                <option key={universidad.id} value={universidad.id}>{universidad.nombre}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Fecha Origen:</label>
            <DatePicker
              selected={tituloData.fechor}
              showYearDropdown
              showMonthDropdown
              yearDropdownItemNumber={120}
              scrollableYearDropdown
              minDate={new Date(1900, 0, 1)}
              maxDate={new Date()}
              onChange={(date) => handleDateChange('fechor', date)}
              dateFormat="dd/MM/yyyy" // Formato de fecha
              isClearable
              placeholderText="Seleccione una fecha"
            />
          </div>
          <div className="form-group">
            <label>Universidad Revalida:</label>
            <select name="univrev" value={tituloData.univrev} onChange={handleChange}>
              <option value="">Seleccione una universidad</option>
              {universidades.map(universidad => (
                <option key={universidad.id} value={universidad.id}>{universidad.nombre}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Fecha Revalida:</label>
            <DatePicker
              selected={tituloData.fechrev}
              showYearDropdown
              showMonthDropdown
              yearDropdownItemNumber={120}
              scrollableYearDropdown
              minDate={new Date(1900, 0, 1)}
              maxDate={new Date()}
              onChange={(date) => handleDateChange('fechrev', date)}
              dateFormat="dd/MM/yyyy" // Formato de fecha
              isClearable
              placeholderText="Seleccione una fecha"
            />
          </div>
          <div className="form-group">
            <label>Fecha Postgrado:</label>
            <DatePicker
              selected={tituloData.fechapos}
              showYearDropdown
              showMonthDropdown
              yearDropdownItemNumber={120}
              scrollableYearDropdown
              minDate={new Date(1900, 0, 1)}
              maxDate={new Date()}
              onChange={(date) => handleDateChange('fechapos', date)}
              dateFormat="dd/MM/yyyy" // Formato de fecha
              isClearable
              placeholderText="Seleccione una fecha"
            />
          </div>
          <div className="form-group">
            <label>Imagen:</label>
            <input
              type="file"
              name="documpdf"
              accept="image/*"
              onChange={handleFileChange}
            />
            <Button variant="secondary" onClick={startCamera}>
              <BsCamera />
            </Button>
            {imagePreview && (
              <Button variant="info" onClick={toggleImageVisibility}>
                {imageVisible ? <BsEyeSlash /> : <BsEye />}
              </Button>
            )}
          </div>
          {imagePreview && imageVisible && (
            <div className="mt-3">
              <Image src={imagePreview} fluid style={{ maxWidth: '100%', height: 'auto' }} />
            </div>
          )}
          {showCameraModal && (
            <Modal show={showCameraModal} onHide={() => setShowCameraModal(false)}>
              <Modal.Header closeButton>
                <Modal.Title>Tomar foto</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <video ref={videoRef} style={{ width: '100%' }} />
                <Form.Group className="mt-3">
                  <Form.Label>Nombre del Archivo</Form.Label>
                  <Form.Control
                    type="text"
                    value={imageName}
                    onChange={(e) => setImageName(e.target.value)}
                    placeholder="Ingrese el nombre del archivo"
                  />
                </Form.Group>
                <Button onClick={captureImage}>Capturar</Button>
              </Modal.Body>
            </Modal>
          )}
          <div className="button-group">
            <button
              type="button"
              className="button btn-cancel"
              onClick={() => closeFormModal(false)}
            >
              Cancelar
            </button>
            <button type="submit" className="button btn-accent">Guardar</button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default TitulosForm;